import React, { useState, useEffect } from 'react';
import { Check, ShoppingCart, Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getImageStyle } from '../components/ImagePositionEditor';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Book = () => {
  const [bookInfo, setBookInfo] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [sections, setSections] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookRes, testimonialsRes, sectionsRes, pageContentRes] = await Promise.all([
        axios.get(`${API_URL}/book/info`),
        axios.get(`${API_URL}/testimonials`),
        axios.get(`${API_URL}/site-sections`),
        axios.get(`${API_URL}/page-content/book`)
      ]);
      setBookInfo(bookRes.data);
      setTestimonials(testimonialsRes.data);
      setSections(sectionsRes.data);
      setPageContent(pageContentRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setBookInfo({
        title: 'Explant',
        author: 'Lindsey Mayr',
        price: 29.99,
        currency: 'USD',
        priceDisplay: '$29.99 USD',
        description: 'A comprehensive guide for women navigating breast implant illness and the explant journey.',
        coverImage: '',
        purchase_link: 'https://explant.healing-women.com/',
        features: ['Personal stories and experiences', 'Medical insights on breast implant illness', 'Step-by-step explant preparation guide', 'Recovery and healing protocols', 'Emotional support strategies']
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !bookInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  const features = bookInfo.features || [
    'Personal stories and experiences',
    'Medical insights on breast implant illness',
    'Step-by-step explant preparation guide',
    'Recovery and healing protocols',
    'Emotional support strategies'
  ];
  const bookControls = sections?.book || {};
  const purchaseLink = bookInfo.purchase_link || bookInfo.purchaseLink || 'https://explant.healing-women.com/';
  const buttonText = bookControls.buttonText || bookInfo.buttonText || 'Purchase on Friesen Press';
  const priceDisplay = bookInfo.priceDisplay || `${bookInfo.price || ''} ${bookInfo.currency || ''}`.trim();
  const heroTitle = pageContent?.title || `My Book: ${bookInfo.title}`;
  const heroDescription = pageContent?.description || bookInfo.description;

  if (bookControls.enabled === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 via-lavender-50 to-cream-50 flex items-center justify-center px-4">
        <Card className="max-w-2xl border-0 shadow-xl bg-white">
          <CardContent className="p-10 text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-serif text-gray-900">
              {bookControls.disabledTitle || 'Book page is currently unavailable'}
            </h1>
            <p className="text-lg text-gray-600">
              {bookControls.disabledMessage || 'Please check back soon for updated book information.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="book-page">
      <section className="py-20 bg-gradient-to-br from-sage-50 via-lavender-50 to-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-serif text-gray-900">{heroTitle}</h1>
            {heroDescription && <p className="text-xl text-gray-600 max-w-3xl mx-auto">{heroDescription}</p>}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="sticky top-24">
              <div className="bg-sage-50 rounded-3xl p-12 shadow-2xl">
                {bookInfo.coverImage ? (
                  <img src={bookInfo.coverImage} alt={bookInfo.title} className="w-full rounded-2xl shadow-xl" style={getImageStyle(bookInfo.coverImagePosition)} />
                ) : (
                  <div className="w-full aspect-[3/4] rounded-2xl bg-sage-100 flex items-center justify-center">
                    <div className="text-center p-8">
                      <h3 className="text-3xl font-serif text-sage-700">{bookInfo.title}</h3>
                      <p className="text-sage-600 mt-2">by {bookInfo.author}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4" data-testid="book-title">{bookInfo.title}</h2>
                <p className="text-xl text-gray-600">By {bookInfo.author}</p>
              </div>

              <div className="flex items-center space-x-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 fill-amber-400 text-amber-400" />
                ))}
              </div>

              <p className="text-lg text-gray-700 leading-relaxed">{bookInfo.description}</p>

              <div className="bg-sage-50 rounded-2xl p-8 space-y-4">
                <h3 className="text-2xl font-semibold text-gray-900">What's Inside:</h3>
                <ul className="space-y-3">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Check className="h-6 w-6 text-sage-700 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Card className="border-2 border-sage-200 shadow-lg">
                <CardContent className="p-8 space-y-6">
                  {bookControls.showPrice !== false && priceDisplay && (
                    <div className="flex items-baseline space-x-3">
                      <span className="text-5xl font-bold text-gray-900" data-testid="book-price">{priceDisplay}</span>
                    </div>
                  )}
                  <div className="pt-4 border-t border-gray-200">
                    <a href={purchaseLink} target="_blank" rel="noopener noreferrer">
                      <Button size="lg" className="w-full bg-sage-700 hover:bg-sage-800 text-white py-6 text-lg rounded-xl transition-all duration-300 hover:shadow-lg" data-testid="purchase-book-btn">
                        <ShoppingCart className="mr-2 h-5 w-5" />
                        {buttonText}
                      </Button>
                    </a>
                    {bookControls.redirectNote !== '' && (
                      <p className="text-sm text-gray-500 text-center mt-3">
                        {bookControls.redirectNote || "You'll be redirected to Friesen Press to complete your purchase"}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {bookControls.showReviews !== false && testimonials.length > 0 && (
        <section className="py-20 bg-sage-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-serif text-gray-900 text-center mb-12">Reader Reviews</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="border-0 shadow-lg bg-white">
                  <CardContent className="p-8 space-y-4">
                    <div className="flex space-x-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 leading-relaxed italic">"{testimonial.text}"</p>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Book;
